// @wbwl/contract: the JSON the API sends and accepts. apps/api builds these shapes and apps/web
// reads them, so a change here is checked on both sides by `npm run typecheck`.
//
// Every route lives under /api/v1. Requests that change state are POST/PATCH/DELETE with a JSON
// body and `Content-Type: application/json` (the API rejects anything else, which is the CSRF
// guard alongside the SameSite=Lax session cookie). Errors are `{ error: ErrorCode, ... }` with a
// 4xx status.
import type { Burn, GoldTier, Phase, PhoenixResult, RowsEnd, RunEnd, WordScore } from '@wbwl/rules'

export type { Burn, GoldTier, Phase, PhoenixResult, RowsEnd, RunEnd, WordScore }

// ---------- players & sign-in ----------

export type PlayerKind = 'guest' | 'member'
export type Provider = 'email' | 'google'
/** Where a profile photo came from: the Google account, Gravatar, or an upload. */
export type AvatarSource = 'google' | 'gravatar' | 'upload'

/** GET /api/v1/me. Creates a guest (and sets the session cookie) on a first visit. */
export interface Me {
  readonly id: string
  readonly displayName: string
  readonly kind: PlayerKind
  /** Only for members; never shown to other players. */
  readonly email: string | null
  readonly providers: readonly Provider[]
  /** Consecutive daily editions finished on their day, ending today or yesterday. */
  readonly streak: number
  /** A member whose email is in the server's ADMINS list; they can open the admin view. */
  readonly admin: boolean
  /** Your profile photo's URL (on this site), or null for initials. */
  readonly avatar: string | null
  readonly avatarSource: AvatarSource | null
  /** Your public profile's handle (/p/<handle>), members only; guests have no profile. */
  readonly profile: string | null
}

/**
 * Profile photos (members only). Photos are copied to this site, never linked from Google or
 * Gravatar, and served at GET /api/v1/avatars/:playerId?v=<version> (cached for good: a new photo
 * gets a new URL). Every `avatar` field in this file is such a URL, or null for initials.
 *
 * POST /api/v1/me/avatar → Me: an upload, or your Google photo or Gravatar copied in again.
 * DELETE /api/v1/me/avatar → Me: back to initials; signing in again won't bring a photo back.
 *
 * Google sign-in copies your Google photo, and email sign-in your Gravatar if you have one, unless
 * you removed your photo, uploaded one, or have one from the other.
 */
export type AvatarRequest =
  /** A JPEG, PNG or WebP as a base64 data URL, up to 256 KB (the app sends a 256 px square). */
  | { readonly image: string }
  | { readonly source: 'google' | 'gravatar' }

/** PATCH /api/v1/me */
export interface UpdateMeRequest {
  /** 2–24 characters: letters, numbers, spaces, . _ - */
  readonly displayName: string
}

/** GET /api/v1/auth/config: which sign-in options this server has configured. */
export interface AuthConfig {
  readonly google: boolean
  readonly email: boolean
}

/** POST /api/v1/auth/email → 204. Sends a sign-in link that is good for 15 minutes. */
export interface EmailSignInRequest {
  readonly email: string
}

/**
 * POST /api/v1/auth/email/verify → 204 and the session cookie, or 400 expired. Spends the token
 * from the link and signs in the browser that sends it (the confirm page's button).
 */
export interface EmailVerifyRequest {
  readonly token: string
}

// Redirect flows (no JSON):
//   GET  /api/v1/auth/google                → 302 to Google
//   GET  /api/v1/auth/google/callback       → 302 to /?signin=ok | /signin?error=google
//   GET  /api/v1/auth/email/verify?token=   → 302 to /signin/confirm#token= | /signin?error=expired
//                                             (spends nothing: mail scanners open links too)
//   POST /api/v1/auth/logout                → 204 (the next request starts a fresh guest)
// Signing in as a guest attaches the account to that guest. If the account already exists, the
// guest's games move into it (except editions the account already played) and the guest closes.

// ---------- editions ----------

export interface RankStep {
  readonly name: string
  /** Share of the edition's best score, 0–1. */
  readonly pct: number
  /** Points needed: round(best × pct). */
  readonly at: number
}

/** GET /api/v1/editions/today and GET /api/v1/editions/:number (only up to today's). */
export interface EditionInfo {
  readonly number: number
  /** YYYY-MM-DD, in Pacific Time. */
  readonly date: string
  readonly gold: Readonly<Record<string, GoldTier>>
  readonly burns: readonly Burn[]
  readonly rulesVersion: number
  readonly wordsVersion: number
  /** The edition's line, e.g. "K and Y run hot today." */
  readonly headline: string
  /** The words of `headline` that Today sets in burn-red italic, e.g. "hot" (null for none). */
  readonly headlineEmphasis: string | null
  /** Best total the nightly solver found; the ranks are set from it. */
  readonly best: number
  readonly ranks: readonly RankStep[]
  readonly isToday: boolean
  /** ISO time the next edition goes up (midnight Pacific after this one). */
  readonly nextAt: string
  /** Ranked players who finished it, and their average total (null before anyone finishes). */
  readonly players: number
  readonly average: number | null
  /**
   * The solver's line, once the edition's day is over, for a player who has finished it (yesterday's
   * for anyone playing, for the Today card); otherwise null, so an archive game isn't spoiled.
   */
  readonly bestLine: { readonly rows: readonly string[]; readonly phoenix: string | null; readonly total: number } | null
  /**
   * The leaderboard's #1 play (ties go as the board orders them), shown when bestLine is; null
   * otherwise, or when nobody finished the edition on its day.
   */
  readonly topLine: TopLine | null
}

export interface TopLine {
  readonly playerId: string
  readonly displayName: string
  readonly avatar: string | null
  readonly rows: readonly string[]
  /** The raised phoenix, or null when it was skipped or the ashes spelled nothing. */
  readonly phoenix: string | null
  readonly total: number
}

/**
 * The day's +3 gold letters as its headline names them: "K", "K and Y" ('' with none). The API
 * writes headlines with it, and Today keeps the phrase on one line.
 */
export function headlineLetters(gold: Readonly<Record<string, GoldTier>>): string {
  const hot = Object.keys(gold).filter((l) => gold[l] === 3).sort()
  return hot.length > 1 ? `${hot.slice(0, -1).join(', ')} and ${hot[hot.length - 1]}` : (hot[0] ?? '')
}

// ---------- plays ----------

export interface Standing {
  /** Index into EditionInfo.ranks. */
  readonly index: number
  readonly name: string
  readonly next: RankStep | null
  readonly toNext: number
}

/** Where a finished ranked play placed among the edition's ranked players. */
export interface Placement {
  /** 1 = top. Ties share a position. */
  readonly position: number
  readonly players: number
  /** "Top N%": ceil(position / players × 100). */
  readonly topPercent: number
}

/**
 * GET /api/v1/plays/:number → your play of that edition (an empty one if you haven't started).
 * POST /api/v1/plays/:number/moves { word } and /phoenix { word } | { skip: true } return it too.
 */
export interface Play {
  readonly edition: number
  readonly rows: readonly string[]
  /** Burned letters, each once, in burn order. */
  readonly ashes: readonly string[]
  readonly phase: Phase
  readonly rowsEnd: RowsEnd | null
  readonly phoenix: string | null
  readonly phoenixResult: PhoenixResult | null
  readonly scores: {
    readonly rows: readonly WordScore[]
    readonly rowsTotal: number
    readonly phoenix: WordScore | null
    readonly total: number
  }
  readonly standing: Standing
  /**
   * False for archive plays (not finished on the edition's day): they don't count on leaderboards,
   * streaks or stats. An unfinished play turns false once its day is over.
   */
  readonly ranked: boolean
  readonly startedAt: string | null
  readonly finishedAt: string | null
  /** Set once a ranked play is done. */
  readonly placement: Placement | null
}

export interface MoveRequest {
  readonly word: string
}

export type PhoenixRequest = { readonly word: string } | { readonly skip: true }

// ---------- leaderboards ----------

export type LeaderboardScope = 'today' | 'week'

export interface LeaderboardEntry {
  readonly position: number
  readonly playerId: string
  readonly displayName: string
  readonly avatar: string | null
  readonly total: number
  /** Rank name for that edition ('today'); null for 'week'. */
  readonly rankName: string | null
  readonly isMe: boolean
  /** The badge they show beside their name (members, Silver and up), or null. */
  readonly badge: ShownBadge | null
  /** Their profile's handle (/p/<handle>); null for guests, who have none. */
  readonly profile: string | null
}

/** GET /api/v1/leaderboards/:number?scope=today|week (week = the 7 editions ending at :number). */
export interface Leaderboard {
  readonly edition: number
  readonly scope: LeaderboardScope
  readonly players: number
  readonly average: number | null
  readonly best: number | null
  /** Counts of totals in bins of 50 points, from 0 up to the highest bin with anyone in it ('today' only). */
  readonly histogram: readonly number[]
  /** The page's rows (the top 50 by default). */
  readonly entries: readonly LeaderboardEntry[]
  /** Your row, also when it's outside the page; null if you haven't finished. */
  readonly me: LeaderboardEntry | null
  /** Your row number in the board's order (1-based; ties share a position but not a row), so a client knows how far down you are. */
  readonly meRow: number | null
  /** The page: entries are rows offset+1 onward (GET ?offset=&limit=, 50 by default, at most 200). */
  readonly offset: number
}

// ---------- slow burn ----------

/**
 * GET /slow-burn/today and /slow-burn/:n. The day's pattern stays on the server: a run gets the
 * flame columns one row ahead (SlowBurnRun.cols), so the rows after the next really are hidden.
 */
export interface SlowBurnInfo {
  readonly number: number
  /** YYYY-MM-DD */
  readonly date: string
  readonly gold: Readonly<Record<string, GoldTier>>
  readonly rulesVersion: number
  readonly wordsVersion: number
  /** Rows in a run (26). */
  readonly rows: number
  readonly isToday: boolean
  /** When the next Slow Burn goes up (midnight Pacific), ISO. */
  readonly nextAt: string
  /** Finished, ranked runs today. */
  readonly players: number
  /** The most points a run scored, and the deepest row a player reached; null before anyone finishes. */
  readonly best: number | null
  readonly deepest: number | null
  /** The solver's run. Its words show once the day is over; until then rows is null. */
  readonly solver: { readonly depth: number; readonly points: number; readonly rows: readonly string[] | null }
}

/** GET /slow-burn/:n/run, and the answer to every move. */
export interface SlowBurnRun {
  readonly edition: number
  readonly rows: readonly string[]
  /** One letter per row, in the order they burned. */
  readonly ashes: readonly string[]
  readonly ended: RunEnd | null
  /** Flame columns known to you: every row played, the current one and the next (all played ones once ended). */
  readonly cols: readonly number[]
  readonly scores: { readonly rows: readonly WordScore[]; readonly points: number }
  readonly depth: number
  /** Counts on the board: started and finished on its day. */
  readonly ranked: boolean
  readonly startedAt: string | null
  readonly finishedAt: string | null
  /** Where a finished, ranked run sits on today's board. */
  readonly placement: Placement | null
  /**
   * The endgame fuse, once fewer than 100 words fit: the row up next must be played by `endsAt`
   * (`msLeft` from this answer, so a client's own clock doesn't matter), or the run ends as
   * fuse_out. null before then, and once the run has ended.
   */
  readonly fuse: { readonly endsAt: string; readonly msLeft: number; readonly seconds: number } | null
}

export type SlowBurnScope = 'today' | 'ever'

export interface SlowBurnEntry {
  readonly position: number
  readonly playerId: string
  readonly displayName: string
  readonly avatar: string | null
  readonly depth: number
  readonly points: number
  readonly ended: RunEnd
  /** The run's Slow Burn No. (on the best-ever board, each player's best run). */
  readonly edition: number
  readonly isMe: boolean
}

/** GET /slow-burn/:n/board?scope=today|ever. Ordered by points, then depth. */
export interface SlowBurnBoard {
  readonly edition: number
  readonly scope: SlowBurnScope
  readonly players: number
  readonly best: number | null
  readonly deepest: number | null
  /** Runs ending on each row: index 0 is row 1 (today only; empty for ever). */
  readonly histogram: readonly number[]
  readonly entries: readonly SlowBurnEntry[]
  readonly me: SlowBurnEntry | null
  /** Your row number in the board's order (1-based). */
  readonly meRow: number | null
  /** The page: entries are rows offset+1 onward (GET ?offset=&limit=, 50 by default, at most 200). */
  readonly offset: number
}

// ---------- stats ----------

export interface CalendarDay {
  readonly date: string
  readonly edition: number
  /** Rank index of your finished play, or null if you didn't finish it. */
  readonly rankIndex: number | null
  readonly total: number | null
  readonly ranked: boolean
}

/** GET /api/v1/me/stats */
export interface Stats {
  /**
   * Plays finished on their edition's day. This and everything below but `history` leave archive
   * plays out.
   */
  readonly played: number
  readonly streak: number
  readonly bestStreak: number
  readonly average: number | null
  readonly best: { readonly total: number; readonly edition: number } | null
  /** One entry per rank step, top rank last. */
  readonly rankCounts: readonly { readonly name: string; readonly count: number }[]
  /** Every edition you've finished, archive plays too, newest first: the History tab builds its months from these. */
  readonly history: readonly CalendarDay[]
  /** Your three best-scoring single words. */
  readonly bestWords: readonly { readonly word: string; readonly total: number; readonly edition: number }[]
  /** Your best place on an edition's leaderboard (ties share a place, as on Results), then the bigger field, then the latest. */
  readonly bestFinish: { readonly position: number; readonly players: number; readonly edition: number } | null
  /** The median of your Top x% results. */
  readonly usualTopPercent: number | null
  /** Phoenixes raised, out of the games where you raised or skipped one. */
  readonly phoenix: { readonly raised: number; readonly tries: number }
  readonly form: StatsForm
  /** The first-row word you've played most, once it has opened 3 games. */
  readonly opener: { readonly word: string; readonly times: number } | null
  /** Different words you've played, rows and raised phoenixes, out of `wordsPlayed`. */
  readonly differentWords: number
  readonly wordsPlayed: number
}

/** How much of the solver's best you score, as a percent of the edition's best total. */
export interface StatsForm {
  /** The edition number of days[0]. */
  readonly from: number
  /** The last 30 editions, oldest first and today last; null where you didn't finish one on its day. */
  readonly days: readonly ({ readonly percent: number; readonly rankIndex: number } | null)[]
  /** Your average over those days, or null if you finished none. */
  readonly average: number | null
  /** Your average over the 30 editions before, when you finished at least 5 in each stretch. */
  readonly before: number | null
  readonly allTime: number | null
  /** Your highest share, the earliest if tied. */
  readonly best: { readonly percent: number; readonly edition: number } | null
}

// ---------- discussion ----------

export interface Comment {
  readonly id: string
  readonly parentId: string | null
  readonly author: { readonly id: string; readonly displayName: string; readonly avatar: string | null }
  /** The author's result for this edition, if they finished it. */
  readonly authorTotal: number | null
  readonly authorRankName: string | null
  readonly body: string
  readonly createdAt: string
  readonly mine: boolean
}

/** GET /api/v1/editions/:number/comments. Locked (and empty) until you've finished the edition. */
export interface CommentThread {
  readonly locked: boolean
  readonly count: number
  readonly comments: readonly Comment[]
}

/** POST /api/v1/editions/:number/comments → Comment. DELETE /api/v1/comments/:id → 204 (your own). */
export interface NewComment {
  /** 1–1000 characters. */
  readonly body: string
  readonly parentId?: string | null
}

/** POST /api/v1/feedback → 204 */
export interface FeedbackRequest {
  /** 1–2000 characters. */
  readonly body: string
  /**
   * A note from a rating ask, written after a thumbs up or down: its mode, that thumbs, and the
   * edition of the game whose results it opened on. All three or none.
   */
  readonly mode?: RatingMode
  readonly rating?: Thumbs
  readonly edition?: number
}

// ---------- ratings ----------
// Once per mode the app asks how you're liking it, a moment after a game's results open: the
// daily's once you've finished two games (on their day or from the archive), Slow Burn's once
// you've finished a run. It asks only once, whether you answer or close it.

export const RATING_MODES = ['daily', 'slow_burn'] as const
export type RatingMode = (typeof RATING_MODES)[number]
export type Thumbs = 'up' | 'down'
/** A thumbs, or none for "Prefer not to say". */
export type Rating = Thumbs | 'none'

/** GET /api/v1/me/rating/:mode */
export interface RatingAsk {
  /** You've finished enough of the mode and haven't been asked yet. */
  readonly ask: boolean
}

/**
 * POST /api/v1/me/rating/:mode → 204. The app sends a null rating as the ask opens, which is what
 * keeps it from opening again, then the answer if you pick one.
 */
export interface RatingRequest {
  readonly rating: Rating | null
}

// ---------- sharing ----------
// A shared result ends with a link of its own, https://<domain>/s/<code>, so the admin view can
// count shares and the visits they bring. The browser makes the code when the share sheet opens.
// GET /s/<code> counts a visit (not the sharer's own, nor a link-preview bot's) and redirects to
// the front page tagged utm_source=share, so a newcomer's first touch says where they came from.

/** A share link's code is this many letters and digits (the shares table checks it too). */
export const SHARE_CODE_LENGTH = 6
// No 0/O or 1/l/I, so a code read off a screen survives retyping.
const SHARE_ALPHABET = 'ABCDEFGHJKLMNPQRSTUVWXYZabcdefghijkmnopqrstuvwxyz23456789'
const SHARE_CODE = new RegExp(`^[A-Za-z0-9]{${SHARE_CODE_LENGTH}}$`)

/** A share link's code from SHARE_CODE_LENGTH random bytes, which the browser makes. */
export const shareCodeFrom = (bytes: ArrayLike<number>): string =>
  Array.from(bytes, (b) => SHARE_ALPHABET[b % SHARE_ALPHABET.length]).join('')

/** Whether a share link's code has the right form (the server checks only that, not the alphabet). */
export const isShareCode = (s: unknown): s is string => typeof s === 'string' && SHARE_CODE.test(s)

/**
 * 'link': Send or Copy link sent the share's link. 'text' (the emoji grid, which ended with the
 * link, copied) and 'image' (the share image saved) are from the share sheet before it sent
 * challenges, and still accepted from a copy of the app opened before then.
 */
export type ShareVia = 'link' | 'text' | 'image'

/**
 * POST /api/v1/shares → 204. The share sheet sends its code and your challenge once it has one,
 * which makes the link (/s/<code>) open that challenge; then again with `via` each time something
 * goes out. Sending the same code again only notes what's new. 403 locked until you've finished
 * the edition; 400 for a challenge that isn't yours on that edition.
 */
export interface ShareRequest {
  readonly code: string
  readonly edition: number
  /** What went out; left out when the sheet only registers its link. */
  readonly via?: ShareVia
  /** Your challenge on the edition (its code), which the link opens. */
  readonly challenge?: string
  /** With via 'link': the message carried the ash riddle. */
  readonly riddle?: boolean
}

// ---------- challenges ----------
// A player who finished an edition challenges friends to it with a link (/c/<code>), or invites
// players they've had a challenge with before. Whoever joins plays the same edition, and they
// compare. Records count each pair of players once per edition, however many challenges they share.

export type ChallengeStatus = 'invited' | 'joined'
/** A result from your side: won, lost, drawn. */
export type MatchResult = 'w' | 'l' | 'd'

/**
 * Your record against another player, over every challenge you've both finished. A result you
 * haven't seen yet (it came in since you last opened that edition's challenges) waits until you do.
 */
export interface HeadToHead {
  readonly wins: number
  readonly losses: number
  readonly ties: number
  /** Your last five results against them, oldest first. */
  readonly form: readonly MatchResult[]
}

/** A finished play, word for word. */
export interface ChallengePlay {
  readonly rows: readonly string[]
  readonly phoenix: string | null
  readonly phoenixResult: PhoenixResult | null
  readonly scores: {
    readonly rows: readonly WordScore[]
    readonly phoenix: WordScore | null
    readonly total: number
  }
}

export interface ChallengePlayer {
  readonly id: string
  readonly displayName: string
  readonly avatar: string | null
  readonly status: ChallengeStatus
  readonly creator: boolean
  readonly mine: boolean
  /** Rows played so far. */
  readonly rows: number
  /** Where their play is: null before they start, then play, phoenix (raising it) and done. */
  readonly phase: Phase | null
  readonly done: boolean
  /** Once they finish. */
  readonly total: number | null
  readonly rankName: string | null
  /** Their share grid once they finish, one string per row ('b' burned, 'g' gold, '.' plain): no words. */
  readonly grid: readonly string[] | null
  /** Finished since you last opened one of your challenges on this edition. */
  readonly fresh: boolean
  /** Their words: only once you've joined and finished too, and only from GET /challenges/:code. */
  readonly play: ChallengePlay | null
  /** Your record against them; null for yourself, or when you aren't in the challenge. */
  readonly record: HeadToHead | null
  readonly badge: ShownBadge | null
  readonly profile: string | null
}

/**
 * GET /api/v1/challenges/:code. Anyone with the link sees who joined and their scores; words and
 * the chat open to players in it who finished. For a player in it, `players` also holds everyone
 * from their other challenges on the same edition (one list for the day), and opening it marks
 * all of that edition's results seen. Players come finished first (best total first), then
 * playing, then not started, then invited (shown only to players in it).
 */
export interface Challenge {
  readonly code: string
  readonly edition: number
  readonly creator: { readonly id: string; readonly displayName: string; readonly avatar: string | null }
  readonly createdAt: string
  /** Nobody new can join after this (7 days after it was made). */
  readonly expiresAt: string
  /** Whether someone new can still join: not expired and not full (20 players). */
  readonly open: boolean
  /** You: not in it, invited in the app, or in it. */
  readonly me: 'none' | ChallengeStatus
  /** Whether you've finished the edition (which opens the words and the chat to players in it). */
  readonly finished: boolean
  readonly players: readonly ChallengePlayer[]
  /** Comments in its chat (the group's: see ChallengeThread). */
  readonly comments: number
  /**
   * The creator's ash riddle, once they've sent it in a share: their ashes, A to Z, and what the
   * phoenix they raised from them scored. Null otherwise, and in lists (only GET /challenges/:code
   * has it).
   */
  readonly riddle: { readonly ashes: readonly string[]; readonly points: number } | null
}

/**
 * GET /api/v1/challenges/:code/comments. One chat for a group: the edition's challenges linked to
 * this one by players who joined both, however many steps away, so everyone in any of them reads
 * the same thread. Locked (and empty) until you're in this challenge and have finished the edition.
 */
export interface ChallengeThread extends CommentThread {
  /** Who reads it (you too): everyone who joined one of the group's challenges. Empty while locked. */
  readonly players: readonly { readonly id: string; readonly displayName: string }[]
}

/**
 * POST /api/v1/challenges → Challenge: 201 for a new one, 200 when you already have one for that
 * edition (one link per player per edition). 403 locked until you've finished the edition.
 */
export interface NewChallenge {
  readonly edition: number
}

/** POST /api/v1/challenges/:code/invite → Challenge. Only someone you've been in a challenge with. */
export interface ChallengeInviteRequest {
  readonly playerId: string
}

// POST /api/v1/challenges/:code/join      → Challenge (a first visit joins as a new guest);
//                                           403 challenge_closed after expiresAt, 409 challenge_full
// POST /api/v1/challenges/:code/decline   → 204, drops your in-app invite
// GET  /api/v1/challenges/:code/comments  → ChallengeThread (locked until you're in it and finished)
// POST /api/v1/challenges/:code/comments  → Comment ({ body }; no replies)
// DELETE /api/v1/challenge-comments/:id   → 204 (your own)

/** GET /api/v1/editions/:number/challenges: the challenges you're in or invited to for an edition. */
export interface ChallengeList {
  readonly challenges: readonly Challenge[]
}

/** One edition's challenges that you're in or invited to, yours first. */
export interface EditionChallenges {
  readonly edition: number
  /** YYYY-MM-DD, Pacific. */
  readonly date: string
  readonly challenges: readonly Challenge[]
}

/**
 * GET /api/v1/me/challenges: the last 7 editions' challenges you're in or invited to, newest
 * edition first (links stay open for 7 days, so an invite can be for an edition you missed).
 */
export interface MyChallenges {
  readonly editions: readonly EditionChallenges[]
}

/** Another player as Rivals shows them: their badge and profile too. */
export interface RivalPlayer {
  readonly id: string
  readonly displayName: string
  readonly avatar: string | null
  readonly badge: ShownBadge | null
  readonly profile: string | null
}

export interface Rival {
  readonly player: RivalPlayer
  readonly record: HeadToHead
  /** The latest edition you both finished in a shared challenge. */
  readonly last: { readonly edition: number; readonly mine: number; readonly theirs: number }
}

/** GET /api/v1/me/rivals: everyone you've finished a challenge with, most recent first. */
export interface Rivals {
  readonly rivals: readonly Rival[]
}

export interface RivalMatch {
  readonly edition: number
  /** YYYY-MM-DD, Pacific. */
  readonly date: string
  readonly mine: number
  readonly theirs: number
  /** A challenge you were both in for that edition. */
  readonly code: string
}

/** GET /api/v1/rivals/:playerId: your history with one player (404 if you have none). */
export interface Rivalry {
  readonly player: RivalPlayer
  readonly record: HeadToHead
  /** Average totals over your matches. */
  readonly average: { readonly mine: number; readonly theirs: number }
  /** Your widest win and theirs; null when there's none. */
  readonly biggestWin: { readonly edition: number; readonly margin: number } | null
  readonly biggestLoss: { readonly edition: number; readonly margin: number } | null
  /** Newest first. */
  readonly matches: readonly RivalMatch[]
}

// ---------- reminders ----------

/** GET /api/v1/reminders: the key browsers subscribe to push with; null when the server has none (reminders are off). */
export interface ReminderConfig {
  readonly publicKey: string | null
}

/** A browser's push subscription, as PushSubscription.toJSON() gives it. */
export interface PushSubscriptionJson {
  readonly endpoint: string
  readonly keys: { readonly p256dh: string; readonly auth: string }
}

/**
 * POST /api/v1/me/reminder → Reminder, or 422 invalid_subscription. Turns on (or refreshes) this
 * browser's daily reminder: one notification a day at `hour` (0–23) in `timeZone`, only while
 * today's edition is unfinished. Without `hour` a saved reminder keeps its hour and a new one gets 9.
 * DELETE /api/v1/me/reminder { endpoint } → 204 turns it off.
 */
export interface ReminderRequest {
  readonly subscription: PushSubscriptionJson
  /** IANA name, from Intl.DateTimeFormat().resolvedOptions().timeZone. */
  readonly timeZone: string
  readonly hour?: number
  /** The endpoint this subscription replaces (the service worker's pushsubscriptionchange): its hour carries over and it ends. */
  readonly replaces?: string
}

export interface Reminder {
  readonly hour: number
}

/**
 * What a push service did with a push: sent (took it to deliver), retry (an outage: 429, 5xx or no
 * answer), ended (the subscription is gone, so the reminder is off) or refused (anything else).
 */
export type ReminderOutcome = 'sent' | 'retry' | 'ended' | 'refused'

/**
 * POST /api/v1/me/reminder/test { endpoint } → ReminderTest, or 404 not_found when this browser's
 * reminder isn't saved for you. Sends this browser a test notification now.
 */
export interface ReminderTest {
  /** The push's id, which the service worker reports when it arrives. */
  readonly id: string
  readonly outcome: ReminderOutcome
  /** The push service's HTTP status; null when it couldn't be reached. */
  readonly status: number | null
}

/** POST /api/v1/reminders/received { id } → 204: the service worker saying a push reached the browser. */
export interface ReminderReceived {
  readonly id: string
}

// ---------- admin ----------
// Every /api/v1/admin route answers 403 forbidden unless you're a member whose email is in the
// server's ADMINS list (Me.admin).

/** GET /api/v1/admin/overview */
export interface AdminOverview {
  /** Today's edition and how many ranked players have finished it. */
  readonly today: { readonly edition: number; readonly players: number }
  /** Accounts, whether or not they've played. */
  readonly members: number
  /**
   * Guest profiles not merged into an account: those that have played a word, and those that only
   * visited. Every new browser that opens the site gets one, so one person can have several.
   */
  readonly guests: { readonly played: number; readonly visited: number }
  readonly openFeedback: number
  /** How each mode's rating ask went. */
  readonly ratings: Readonly<Record<RatingMode, RatingTally>>
}

/** Everyone a mode's rating ask opened for, by what they did with it. */
export interface RatingTally {
  readonly up: number
  readonly down: number
  /** Prefer not to say. */
  readonly none: number
  /** Closed it without answering. */
  readonly closed: number
  /** Notes written after a thumbs, open or done. */
  readonly notes: number
}

/** GET /api/v1/admin/sources: new visitors (each new browser is one) by where they came from, over the last 14 days. */
export interface AdminSources {
  /** The first day counted (YYYY-MM-DD, Pacific); the last is today. */
  readonly since: string
  /** Newest day first; within a day, the source with the most new players first. */
  readonly rows: readonly SourceRow[]
}

/**
 * Players who first showed up on one day (Pacific, so one edition) from one source. Guests who
 * later signed in to an account that already existed aren't new, so they're left out.
 */
export interface SourceRow {
  /** YYYY-MM-DD in Pacific time. */
  readonly date: string
  readonly edition: number
  /** The utm_source and utm_campaign of the link they first came in on; null without one. */
  readonly source: string | null
  readonly campaign: string | null
  /** For players whose link had no utm_source: the host of the site that sent them, if any. */
  readonly referrer: string | null
  readonly players: number
  /** How many played at least one word that same day (in any puzzle). */
  readonly played: number
  /**
   * The median of how many rows those who played got to that day (the most in any one puzzle they
   * started); null when nobody played.
   */
  readonly medianRows: number | null
  /** How many finished a puzzle that same day. */
  readonly finished: number
  /** How many played the next day's edition on its day; null for today's newcomers. */
  readonly returned: number | null
}

/** The windows GET /api/v1/admin/analytics takes, in days; 1 is today so far. */
export const ANALYTICS_DAYS = [1, 7, 30, 90] as const
export type AnalyticsDays = (typeof ANALYTICS_DAYS)[number]

/** One Slow Burn day on the admin page. Runs by merged guests are left out. */
export interface AdminSlowBurnDay {
  readonly number: number
  /** YYYY-MM-DD */
  readonly date: string
  /** The solver's best run that day. */
  readonly solverPoints: number
  readonly solverDepth: number
  /** Runs with at least one row played. */
  readonly started: number
  /** Of those, ended: burned out, ended early or the fuse ran out. */
  readonly finished: number
  readonly bestPoints: number | null
  readonly medianPoints: number | null
  readonly medianDepth: number | null
  readonly deepest: number | null
  readonly burnedOut: number
  readonly endedEarly: number
  readonly fuseOut: number
  readonly dayOver: number
  /** From the first word to the end, of finished runs. */
  readonly medianMinutes: number | null
}

/** GET /admin/slow-burn?days=1|7|30|90: the Slow Burn tab of the admin page, over the last N Pacific days. */
export interface AdminSlowBurn {
  readonly days: AnalyticsDays
  /** The window's first day, YYYY-MM-DD. */
  readonly since: string
  readonly kpis: {
    /** Players with a run in the window. */
    readonly players: number
    readonly started: number
    readonly finished: number
    readonly medianPoints: number | null
    readonly medianDepth: number | null
    /** Of `players`, those who also played the daily edition on a day they ran Slow Burn. */
    readonly bothGames: number
  }
  /** How the window's runs ended; unfinished runs are today's, still going. */
  readonly ends: { readonly burnedOut: number; readonly endedEarly: number; readonly fuseOut: number; readonly dayOver: number; readonly unfinished: number }
  /** Finished runs by the row they reached: index 0 is row 1. */
  readonly depths: readonly number[]
  /** Newest first. */
  readonly perDay: readonly AdminSlowBurnDay[]
}
/** The days after a player's first play that retention looks at. */
export const RETENTION_OFFSETS = [1, 2, 3, 7, 14, 30] as const

/**
 * GET /api/v1/admin/analytics?days=1|7|30|90: how the game is doing over the last `days` Pacific
 * days, today included. Merged guests are left out everywhere. A "play" is a player's game of one
 * edition; it exists once a word is accepted. Ranked plays were started and finished on their
 * edition's day; a play started on its day but finished later counts as started, not finished.
 */
export interface AdminAnalytics {
  readonly days: AnalyticsDays
  /** The first day counted (YYYY-MM-DD, Pacific); the last is today. */
  readonly since: string
  readonly today: { readonly date: string; readonly edition: number }
  /**
   * The window's totals, and the same for the `days` before it, to compare. That window stops at
   * the same time of day as now, so today so far is measured against part of a day, not a whole one.
   */
  readonly kpis: { readonly current: AnalyticsKpis; readonly previous: AnalyticsKpis }
  /** One per day, oldest first. */
  readonly daily: readonly AnalyticsDay[]
  /** Each edition in the window, newest first. */
  readonly editions: readonly AnalyticsEdition[]
  /** Per-row days, pattern days, then each pattern with any edition in the window. */
  readonly layouts: readonly AnalyticsLayout[]
  /** Players grouped by the day of their first play, newest first. */
  readonly retention: readonly AnalyticsCohort[]
  /** Plays started in each hour of the day (Pacific), 0 to 23. */
  readonly hours: readonly number[]
  /** Players active in the window, by the device of their latest session. iPads that ask for desktop sites count as desktop. */
  readonly devices: { readonly phone: number; readonly tablet: number; readonly desktop: number; readonly unknown: number }
  /** The 10 words played most in plays started in the window. */
  readonly words: { readonly openers: readonly WordCount[]; readonly all: readonly WordCount[]; readonly phoenixes: readonly WordCount[] }
  /** Shares and challenges in the window, the `days` before it, and every day so far. */
  readonly sharing: { readonly current: SharingCounts; readonly previous: SharingCounts; readonly allTime: SharingCounts }
}

/** Each counted by when it happened. */
export interface SharingCounts {
  /** Share sheets that sent something (link sent or copied, grid copied, image saved), and how many of each. */
  readonly shares: number
  readonly sent: number
  readonly copied: number
  readonly saved: number
  /** Shares whose link went out (sent, or in a copied grid), and of those, the ones followed since. */
  readonly linked: number
  readonly linksClicked: number
  /** Visits through share links: not the sharer's own, nor a link-preview bot's, and one a day per link per visitor (their IP and browser). */
  readonly clicks: number
  /** Players whose first visit came through a share link. */
  readonly newPlayers: number
  /** Challenges made: one link per player per edition. */
  readonly challenges: number
  /** Players who joined someone else's challenge, by its link or an in-app invite, and how many of them have finished its edition. */
  readonly accepted: number
  readonly finished: number
}

export interface AnalyticsKpis {
  /** Distinct players who started any play, ranked or archive. */
  readonly activePlayers: number
  /** Plays started on their edition's day, and how many of them were finished that day (ranked). */
  readonly started: number
  readonly finished: number
  /** Visitors first seen in the window (each new browser is one), and how many of them have played a word. */
  readonly newPlayers: number
  readonly newPlayed: number
  /** Players who first signed in (made an account). */
  readonly signUps: number
  /**
   * Players whose first play came in the window and whose next day is over, and how many played that
   * next day. A one-day window has none, so it takes the day before's first-timers and counts those
   * back by the window's end.
   */
  readonly firstPlays: number
  readonly nextDay: number
  /** The median finished ranked score as a share of its edition's best; null when none finished. */
  readonly medianShare: number | null
  /** Comments posted in editions' discussions. */
  readonly comments: number
}

export interface AnalyticsDay {
  readonly date: string
  readonly edition: number
  /** The edition's burn pattern; null on per-row days (or when it was never generated). */
  readonly pattern: string | null
  /** Players who started the day's edition on its day, and how many finished it that day. */
  readonly players: number
  readonly finished: number
  /** Of `players`, those whose first play ever was that day. */
  readonly firstTimers: number
  /** Plays of past editions started that day. */
  readonly archive: number
  readonly newPlayers: number
}

export interface AnalyticsEdition {
  readonly number: number
  readonly date: string
  readonly burns: readonly (readonly [number, number])[]
  readonly pattern: string | null
  /** The solver's best line. */
  readonly best: number
  /** The leaderboard's #1 total; null until someone finishes it on its day. */
  readonly boardBest: number | null
  /** Plays of it started on its day, and how many were finished that day; archive plays of it started since. */
  readonly started: number
  readonly finished: number
  readonly archive: number
  /** Over finished ranked plays; null when none finished. */
  readonly medianTotal: number | null
  readonly medianShare: number | null
  /** Median minutes from first word to finish. */
  readonly medianMinutes: number | null
  /** Finished ranked plays per rank, Rough Draft first. */
  readonly ranks: readonly number[]
  /** Finished ranked plays that raised a phoenix, and that ran out of vowels or words before row six. */
  readonly phoenix: number
  readonly early: number
  readonly comments: number
}

export interface AnalyticsLayout {
  readonly layout: 'rows' | 'pattern'
  /** One pattern's days; null for every day of the layout. */
  readonly pattern: string | null
  readonly editions: number
  readonly averageBest: number
  readonly started: number
  readonly finished: number
  readonly medianShare: number | null
  readonly phoenix: number
  readonly early: number
}

export interface AnalyticsCohort {
  readonly date: string
  readonly players: number
  /** For each RETENTION_OFFSETS day after: how many played that day; null until that day is over. */
  readonly returned: readonly (number | null)[]
}

export interface WordCount {
  readonly word: string
  readonly count: number
}

export type FeedbackStatus = 'open' | 'done'

export interface FeedbackItem {
  readonly id: string
  readonly body: string
  readonly createdAt: string
  /** When an admin marked it done; null while it's open. */
  readonly doneAt: string | null
  /** A note from a rating ask: its mode, the thumbs before it, and the game's edition. Null for one sent from Settings. */
  readonly mode: RatingMode | null
  readonly rating: Thumbs | null
  readonly edition: number | null
  /** Who sent it; null when it came without a session or the player is gone. */
  readonly player: {
    readonly id: string
    readonly displayName: string
    readonly kind: PlayerKind
    readonly email: string | null
  } | null
}

/** GET /api/v1/admin/feedback?status=open|done&before=<id>: newest first, 50 at a time. */
export interface FeedbackPage {
  readonly items: readonly FeedbackItem[]
  /** Pass as ?before= for the next page; null on the last one. */
  readonly next: string | null
}

/** GET /api/v1/admin/avatars: uploaded photos, newest first, to check. DELETE /api/v1/admin/avatars/:playerId → 204. */
export interface AdminAvatars {
  readonly items: readonly {
    readonly player: { readonly id: string; readonly displayName: string; readonly email: string | null }
    readonly avatar: string
    readonly updatedAt: string
  }[]
}

/** GET /api/v1/admin/reminders: who has the daily reminder on, and how the pushes went. */
export interface AdminReminders {
  /** Browsers with the reminder on, by push service, most first. */
  readonly on: readonly { readonly service: string; readonly browsers: number }[]
  /** Daily reminders over the last 14 Pacific days, by push service; newest day first. */
  readonly days: readonly ReminderDay[]
  /** The latest 50 pushes, daily and test, newest first. */
  readonly recent: readonly ReminderSendItem[]
}

export interface ReminderDay {
  /** YYYY-MM-DD in Pacific time. */
  readonly date: string
  /** Who runs the push service: Google, Mozilla, Apple or Microsoft. */
  readonly service: string
  /** Reminders the push service took to deliver. */
  readonly sent: number
  /** Of those, how many the browser said reached it. */
  readonly received: number
  /** Tries an outage put off to the next minute. */
  readonly retried: number
  /** Refused, or the subscription was gone. */
  readonly failed: number
}

export interface ReminderSendItem {
  readonly id: string
  readonly kind: 'daily' | 'test'
  /** The edition a daily reminder was about. */
  readonly edition: number | null
  readonly service: string
  readonly sentAt: string
  readonly status: number | null
  readonly outcome: ReminderOutcome
  /** When the browser said it arrived; null until (or unless) it does. */
  readonly receivedAt: string | null
  readonly player: { readonly id: string; readonly displayName: string; readonly email: string | null }
}

/** PATCH /api/v1/admin/feedback/:id → FeedbackItem */
export interface UpdateFeedbackRequest {
  readonly done: boolean
}

// ---------- achievements ----------

/**
 * The achievement categories, in display order. A category's badge climbs Bronze → Silver → Gold →
 * Ablaze as its achievements are earned (the steps below). The last, the Phoenix, is the badge above
 * the others: one achievement for each of the eight set Ablaze, and no tiers until all eight, when
 * it rises (its steps are all 8). Achievement names and requirements live only on the server: the app
 * gets them from the API, and a secret one nobody has shown you yet never reaches it at all.
 */
export const BADGE_CATEGORIES = ['cub', 'regular', 'archivist', 'headliner', 'wordsmith', 'firekeeper', 'rival', 'legend', 'phoenix'] as const
export type BadgeCategory = (typeof BADGE_CATEGORIES)[number]
export const BADGE_TIERS = ['bronze', 'silver', 'gold', 'blaze'] as const
/** blaze is Ablaze, a finished category. */
export type BadgeTier = (typeof BADGE_TIERS)[number]
/** Each category's name, and the counts where its Bronze, Silver, Gold and Ablaze begin (Ablaze is all of them). */
export const ACHIEVEMENT_CATEGORIES: Readonly<Record<BadgeCategory, { readonly name: string; readonly line: string; readonly steps: readonly [number, number, number, number] }>> = {
  cub: { name: 'Cub Reporter', line: 'Your first week on the paper: the basics, and the features worth finding.', steps: [3, 5, 8, 10] },
  regular: { name: 'The Regular', line: 'Showing up. No skill needed, only days, streaks and points.', steps: [3, 6, 9, 12] },
  archivist: { name: 'The Archivist', line: 'The back issues: every puzzle since No. 0, one at a time or a month in a sitting.', steps: [3, 6, 9, 12] },
  headliner: { name: 'Headliner', line: 'Ranks and big scores, climbing the ladder to the front page.', steps: [3, 6, 9, 12] },
  wordsmith: { name: 'Wordsmith', line: 'The words themselves: rare letters, odd shapes, and thinking like the solver.', steps: [3, 6, 9, 12] },
  firekeeper: { name: 'Firekeeper', line: 'Gold, burns and the phoenix: the parts only this game has.', steps: [4, 8, 12, 16] },
  rival: { name: 'The Rival', line: 'Challenges with friends, and your place on the day’s leaderboard. Other players count once they’ve played on three days.', steps: [3, 6, 9, 12] },
  legend: { name: 'Legend', line: 'Exceptional days. Any one of these is a story worth telling.', steps: [1, 3, 6, 10] },
  phoenix: { name: 'The Phoenix', line: 'The badge above all the others. Each category you set Ablaze rekindles it; at eight it rises from its ashes.', steps: [8, 8, 8, 8] },
}
/** The Phoenix is earned, not climbed: it has no tier until all eight are Ablaze. */
export const PHOENIX: BadgeCategory = 'phoenix'

/** A category's tier with n of its achievements earned; null below Bronze. */
export function tierFor(category: BadgeCategory, n: number): BadgeTier | null {
  let tier: BadgeTier | null = null
  ACHIEVEMENT_CATEGORIES[category].steps.forEach((step, i) => { if (n >= step) tier = BADGE_TIERS[i] })
  return tier
}

/** Only badges at these tiers can show beside a name, and only for members. */
export const SHOWABLE_TIERS: readonly BadgeTier[] = ['silver', 'gold', 'blaze']

/** A badge as it shows beside a name. */
export interface ShownBadge {
  readonly category: BadgeCategory
  readonly tier: BadgeTier
}

/**
 * One achievement in a category's list. A secret that hasn't been earned (by you on your own list;
 * by you, the viewer, on someone else's) comes as key 'secret' with no name or requirement: only its
 * place in the list, and on someone else's list when they earned it.
 */
export interface AchievementItem {
  /** The achievement's id, or 'secret' for a hidden one. */
  readonly key: string
  readonly name: string | null
  readonly requirement: string | null
  readonly secret: boolean
  /** When it was earned (on their list: when they earned it); null if not yet. */
  readonly earnedAt: string | null
  /** Progress toward a count (14 of 50 phoenixes): only on your own list, only while unearned, never for a secret. */
  readonly progress: { readonly have: number; readonly need: number } | null
  /** On someone else's list: whether you've earned it too. Null on your own. */
  readonly mine: boolean | null
}

export interface AchievementCategory {
  readonly id: BadgeCategory
  readonly name: string
  readonly total: number
  readonly earned: number
  /** Null below Bronze. */
  readonly tier: BadgeTier | null
  /** The counts where Bronze, Silver, Gold and Ablaze begin. */
  readonly steps: readonly number[]
  readonly items: readonly AchievementItem[]
}

/** GET /api/v1/me/achievements. A guest has them too, unclaimed until they sign in. */
export interface MyAchievements {
  readonly categories: readonly AchievementCategory[]
  /** Your pick for beside your name: a category, 'none', or null for the default (your rarest at Silver or above). */
  readonly choice: BadgeCategory | 'none' | null
  /** What shows beside your name now; always null for a guest. */
  readonly badge: ShownBadge | null
  readonly claimed: boolean
  /** The share of members (0–100) holding each category at Silver or above. */
  readonly rarity: Readonly<Record<BadgeCategory, number>>
}

/** A newly earned achievement, for the reveal after a game. */
export interface AchievementUnlock {
  readonly id: string
  readonly name: string
  /** What you did, in the past tense. */
  readonly did: string
  readonly category: BadgeCategory
  /** How many of the category you have counting this one, and the category's size. */
  readonly count: number
  readonly total: number
  readonly tierBefore: BadgeTier | null
  readonly tierAfter: BadgeTier | null
  readonly at: string
}

/** An achievement within reach: the Results strip on a day nothing was earned. */
export interface AchievementNear {
  readonly id: string
  readonly name: string
  readonly category: BadgeCategory
  readonly have: number
  readonly need: number
  /** What's left, like "1 more phoenix". */
  readonly left: string
}

/**
 * GET /api/v1/me/achievements/fresh: achievements earned since the last reveal, oldest first.
 * The reveal shows up to three as cards and more as one catch-up card, then marks them shown with
 * POST /api/v1/me/achievements/seen { ids } → 204.
 */
export interface FreshAchievements {
  readonly unlocks: readonly AchievementUnlock[]
  readonly claimed: boolean
  /** The closest achievements still to earn, at most two (never secrets). */
  readonly near: readonly AchievementNear[]
}

export interface SeenAchievementsRequest {
  readonly ids: readonly string[]
}

/** PUT /api/v1/me/badge → MyAchievements. Members only; a category must be at Silver or above. */
export interface BadgeChoiceRequest {
  readonly category: BadgeCategory | 'none' | null
}

/** An achievement on a profile's recent list (a secret the viewer hasn't earned shows as 'secret'). */
export interface RecentAchievement {
  readonly category: BadgeCategory
  readonly key: string
  readonly name: string | null
  readonly secret: boolean
  readonly earnedAt: string
}

/**
 * GET /api/v1/players/:handle: a member's public profile; 404 for guests, who have none. The handle
 * is `<name>-<code>` and only the code matters, so a rename doesn't break a link. Stats count games
 * finished on their day, as the leaderboards do.
 */
export interface PlayerProfile {
  readonly id: string
  readonly handle: string
  readonly displayName: string
  readonly avatar: string | null
  readonly badge: ShownBadge | null
  /** YYYY-MM-DD, Pacific: the day they finished their first game (any edition), or when they joined. */
  readonly since: string
  readonly editions: number
  readonly streak: number
  readonly usualTopPercent: number | null
  readonly stats: {
    readonly bestFinish: { readonly position: number; readonly edition: number } | null
    readonly frontPages: number
    readonly stopThePresses: number
    readonly best: number | null
    readonly phoenix: { readonly raised: number; readonly tries: number }
    readonly allTime: number | null
  }
  readonly form: StatsForm
  readonly categories: readonly AchievementCategory[]
  /** Newest first, at most five. */
  readonly recent: readonly RecentAchievement[]
  /** Whether it's your own. */
  readonly mine: boolean
  /** Your record against them in challenges; null when you have none. */
  readonly record: HeadToHead | null
}

// ---------- errors ----------

export type ErrorCode =
  // moves (from @wbwl/rules)
  | 'wrong_phase' | 'wrong_length' | 'not_a_word' | 'already_played' | 'burned_letter'
  | 'not_in_ashes' | 'repeated_letter'
  // requests
  | 'bad_request' | 'not_found' | 'future_edition' | 'locked' | 'forbidden' | 'rate_limited'
  | 'invalid_email' | 'invalid_name' | 'expired' | 'unsupported_media_type' | 'server_error'
  // challenges
  | 'challenge_closed' | 'challenge_full'
  // reminders
  | 'invalid_subscription'

export interface ApiError {
  readonly error: ErrorCode
  /** For already_played: the row (0-based) that word was played on. */
  readonly row?: number
  /** For burned_letter / not_in_ashes / repeated_letter. */
  readonly letters?: readonly string[]
  readonly message?: string
}
